import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const WIDTH = 1080;
const HEIGHT = 1350;

function GraphicPlayerSpotlightFeature({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  canUseCutouts = false,
}) {
  const {
    highlightPlayer,
    highlightStats,
    homeTeam,
    backgroundPhoto,
    dominantImage,
    dominantImageX,
    dominantImageY,
    dominantImageScale,
    spotlightLabelFont,
    spotlightLabelSize,
    spotlightLabelColor,
  } = graphicData;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;

  const scale = (dominantImageScale ?? 100) / 100;
  const heroFamily = "'Bebas Neue', 'Arial Narrow', Arial, sans-serif";
  const barlowFamily = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const pillFont = spotlightLabelFont
    ? `'${spotlightLabelFont}', 'Arial Narrow', Arial, sans-serif`
    : barlowFamily;
  const pillSize = spotlightLabelSize ?? 32;
  const pillColor = spotlightLabelColor || '#ffffff';
  const pillX = graphicData.spotlightLabelX ?? 800;

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 50%, ${teamColors.secondary} 50%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const statsLines = highlightStats ? highlightStats.split('\n').filter(Boolean) : [];

  return (
    <div
      id="graphic-player-spotlight-feature"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFamily,
      }}
    >
      {/* Layer 1: Background photo */}
      {backgroundPhoto && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: WIDTH,
            height: HEIGHT,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* Layer 2: Dark overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 2,
        }}
      />

      {/* Layer 3: Dominant image */}
      {dominantImage && (
        <img
          src={dominantImage}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: canUseCutouts ? `${dominantImageY ?? 50}%` : '45%',
            left: canUseCutouts ? `${dominantImageX ?? 50}%` : '40%',
            transform: canUseCutouts
              ? `translate(-50%, -50%) scale(${scale})`
              : 'translate(-50%, -50%) scale(1)',
            transformOrigin: 'center center',
            width: '100%',
            height: 'auto',
            maxWidth: 'none',
            overflow: 'visible',
            zIndex: 3,
          }}
        />
      )}

      {/* Layer 4: Left accent bar */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 24,
          height: HEIGHT,
          background: teamColors.secondary,
          zIndex: 4,
        }}
      />

      {/* Layer 5b: PLAYER SPOTLIGHT pill */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: pillX,
          transform: 'translateX(-50%)',
          display: 'inline-block',
          background: teamColors.secondary,
          color: pillColor,
          fontFamily: pillFont,
          fontSize: pillSize,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 3,
          whiteSpace: 'nowrap',
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 24,
          paddingRight: 24,
          zIndex: 5,
        }}
      >
        PLAYER SPOTLIGHT
      </div>

      {/* Layer 5: Top-right player name block */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          right: 60,
          width: 560,
          zIndex: 5,
        }}
      >
        <div
          style={{
            color: '#ffffff',
            fontFamily: heroFamily,
            fontSize: 100,
            fontWeight: 700,
            textTransform: 'uppercase',
            textAlign: 'right',
            lineHeight: 1.1,
          }}
        >
          {highlightPlayer || 'PLAYER NAME'}
        </div>
      </div>

      {/* Layer 6: Bottom-right stats panel */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          right: 60,
          width: 480,
          zIndex: 6,
        }}
      >
        {statsLines.length > 0
          ? statsLines.map((line, index) => (
              <div
                key={index}
                style={{
                  color: '#ffffff',
                  fontFamily: barlowFamily,
                  fontSize: 38,
                  fontWeight: 600,
                  textAlign: 'right',
                  borderBottom: '2px solid rgba(255,255,255,0.25)',
                  paddingBottom: 12,
                  marginBottom: index === statsLines.length - 1 ? 0 : 12,
                }}
              >
                {line}
              </div>
            ))
          : (
            <div
              style={{
                color: '#ffffff',
                fontFamily: barlowFamily,
                fontSize: 38,
                fontWeight: 600,
                textAlign: 'right',
                borderBottom: '2px solid rgba(255,255,255,0.25)',
                paddingBottom: 12,
              }}
            >
              STATS &amp; ACHIEVEMENTS
            </div>
          )}
      </div>

      {/* Layer 7: Team logo (bottom-left) */}
      {homeTeamLogo && (
        <img
          src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
          alt={homeTeam || 'Team'}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            bottom: 60,
            left: 60,
            height: 140,
            width: 'auto',
            zIndex: 7,
          }}
        />
      )}

      {/* Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <img
            src="/PMlogoleft.png"
            alt="Playmaker Media Kit"
            style={{ height: 80, width: 'auto', display: 'block' }}
          />
        </div>
      )}
    </div>
  );
}

export default GraphicPlayerSpotlightFeature;
